import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { IframeSkeleton } from "@/components/ui/iframe-skeleton";
import { ArrowRight, Mail, Phone, MapPin, ChevronDown } from "lucide-react";
import { SiLinkedin } from "react-icons/si";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";

export default function ContactPage() {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    { q: "How can I contact SaaSGama?", a: "You can contact SaaSGama through the contact form to discuss SaaS management, licensing, or software optimization needs." },
    { q: "How quickly can I expect a response?", a: "SaaSGama aims to respond promptly to inquiries and provide assistance based on your business requirements." },
    { q: "Can I request a demo of SaaSGama?", a: "Yes. SaaSGama can provide a personalized demonstration to help businesses understand its capabilities and benefits." },
    { q: "Who can contact SaaSGama?", a: "SaaSGama welcomes inquiries from business leaders, IT teams, finance teams, procurement professionals, and organizations." },
    { q: "Can SaaSGama help with software cost optimization?", a: "Yes. SaaSGama helps businesses gain visibility into software spending and identify opportunities for optimization." },
    { q: "Is SaaSGama suitable for growing businesses?", a: "SaaSGama supports organizations of all sizes looking to improve software visibility and management." },
    { q: "Can I speak with a SaaS management specialist?", a: "Yes. SaaSGama's team can help answer questions related to software management and optimization strategies." },
    { q: "What information should I provide when contacting SaaSGama?", a: "Sharing details about your software environment and business goals helps SaaSGama provide more relevant guidance." },
    { q: "Can SaaSGama support enterprise software management needs?", a: "Yes. SaaSGama helps organizations manage software visibility, spending, licenses, and operational efficiency." },
    { q: "Why should I get in touch with SaaSGama?", a: "SaaSGama helps businesses gain control of their software ecosystem and make more informed technology decisions." },
  ];

  useEffect(() => {
    // Load the iframe script
    const script = document.createElement('script');
    script.src = 'https://go-crm.venpep.net/js/form_embed.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script when component unmounts
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleIframeLoad = () => {
    // Add a small delay to ensure the iframe content is fully rendered
    setTimeout(() => {
      setIsIframeLoaded(true);
    }, 500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>Contact SaaSGama | Get Expert SaaS Management Help</title>
        <meta name="description" content="Contact SaaSGama for expert SaaS management support, license optimization, cost savings, product demos and tailored solutions today." />
        <meta name="keywords" content="SaaS Management Support, License Optimization, SaaS Cost Savings, SaaS Consultation, Software Asset Management" />
        <link rel="canonical" href="https://saasgama.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/contact" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="Contact SaaSGama | Get Expert SaaS Management Help" />
        <meta property="og:description" content="Contact SaaSGama for expert SaaS management support, license optimization, cost savings, product demos and tailored solutions today." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact SaaSGama | Get Expert SaaS Management Help" />
        <meta name="twitter:description" content="Contact SaaSGama for expert SaaS management support, license optimization, cost savings, product demos and tailored solutions today." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "@id": "https://saasgama.com/#organization", "name": "SaaSGama", "url": "https://saasgama.com", "logo": "https://saasgama.com/logo.png", "description": "SaaSGama helps organizations gain visibility into software subscriptions, licenses, spending, and SaaS management.", "contactPoint": [
              { "@type": "ContactPoint", "contactType": "sales", "email": "hello@saasgama.com", "telephone": "+65-8894-3169", "availableLanguage": ["English"] },
              { "@type": "ContactPoint", "contactType": "customer support", "email": "hello@saasgama.com", "availableLanguage": ["English"] }
            ]},
            { "@type": "BreadcrumbList", "@id": "https://saasgama.com/contact#breadcrumb", "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://saasgama.com/contact" }
            ]},
            { "@type": "ContactPage", "@id": "https://saasgama.com/contact#contactpage", "url": "https://saasgama.com/contact", "name": "Contact SaaSGama", "description": "Get in touch with SaaSGama to discuss SaaS management, software visibility, license optimization, and software cost management.", "mainEntity": { "@id": "https://saasgama.com/#organization" } },
            { "@type": "WebPage", "@id": "https://saasgama.com/contact#webpage", "url": "https://saasgama.com/contact", "name": "Contact SaaSGama", "description": "Contact SaaSGama for SaaS management, software optimization, license management, and software visibility solutions.", "breadcrumb": { "@id": "https://saasgama.com/contact#breadcrumb" } },
            { "@type": "FAQPage", "@id": "https://saasgama.com/contact#faq", "mainEntity": faqs.map(faq => ({ "@type": "Question", "name": faq.q, "acceptedAnswer": { "@type": "Answer", "text": faq.a } })) }
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader showBackButton />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6">
              Get in Touch{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                with the SaaSGama Team
              </span>
            </h1>
            <p className="text-base md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              Have questions about SaaSGama? Want to schedule a demo? Our team is here to help you optimize your SaaS spending.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-xl md:text-3xl font-bold mb-6 text-gray-800">Send us a message</h2>
                <p className="text-gray-600 mb-8">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>

                {/* Iframe Contact Form with Loading State */}
                <div className="relative">
                  {!isIframeLoaded && (
                    <div className="absolute inset-0 z-10">
                      <IframeSkeleton height="802px" />
                    </div>
                  )}
                  
                  <div className={`transition-opacity duration-500 ${isIframeLoaded ? 'opacity-100' : 'opacity-0'}`}>
                    <iframe
                      src="https://go-crm.venpep.net/widget/form/vwZHZFvEX1Gq5nY0ngYF"
                      style={{
                        width: '100%',
                        height: '802px',
                        border: 'none',
                        borderRadius: '3px'
                      }}
                      id="inline-vwZHZFvEX1Gq5nY0ngYF"
                      data-layout='{"id":"INLINE"}'
                      data-trigger-type="alwaysShow"
                      data-trigger-value=""
                      data-activation-type="alwaysActivated"
                      data-activation-value=""
                      data-deactivation-type="neverDeactivate"
                      data-deactivation-value=""
                      data-form-name="SAAS Gama Website Contact Form"
                      data-height="802"
                      data-layout-iframe-id="inline-vwZHZFvEX1Gq5nY0ngYF"
                      data-form-id="vwZHZFvEX1Gq5nY0ngYF"
                      title="SAAS Gama Website Contact Form"
                      onLoad={handleIframeLoad}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-xl md:text-3xl font-bold mb-6 text-gray-800">Contact Information</h2>
                <p className="text-gray-600 mb-8">
                  Prefer to reach out directly? Here are multiple ways to get in touch with our team.
                </p>

                <div className="space-y-6">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Mail className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800 mb-2">Email Us</h3>
                          <p className="text-gray-600 mb-2">For general inquiries and support</p>
                          <a href="mailto:hello@saasgama.com" className="text-blue-600 hover:text-blue-700 font-medium">
                            hello@saasgama.com
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <Phone className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800 mb-2">Call Us</h3>
                          <p className="text-gray-600 mb-2">Speak with our sales team</p>
                          <a href="tel:+6588943169" className="text-blue-600 hover:text-blue-700 font-medium">
                            +65 8894 3169
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <MapPin className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800 mb-2">Visit Us</h3>
                          <p className="text-gray-600 mb-2">Our headquarters</p>
                          <p className="text-gray-700">
                            5 Shenton Wy, #10-01<br />
                            UIC Building<br />
                            Singapore 068808
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>


                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-3 text-gray-800">Frequently Asked Questions</h2>
              <p className="text-base md:text-xl text-gray-600">Quick answers to common questions about SaaSGama</p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="font-semibold text-gray-800 text-sm md:text-base pr-4">{faq.q}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-blue-600 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-gray-600 text-sm md:text-base border-t border-gray-100 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-5xl font-bold mb-6">
              Ready to get started?
            </h2>
            <p className="text-base md:text-xl mb-8 text-blue-100">
              Start optimizing your SaaS spending today and take control of your software investments
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg"
                asChild
              >
                <Link href="/register" className="flex items-center">
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 text-lg font-semibold rounded-xl transition-all bg-white"
                onClick={() => window.open('mailto:hello@saasgama.com')}
              >
                Email Sales Team
              </Button>
            </div>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}
